import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CardTermsDetail,
  ConsignmentFeePolicy,
  ConsignmentAgreement,
  ConsignmentInventoryItem,
  ConsignmentTermsApplyResult,
  ConsignmentTermsDraft,
  ConsignmentTermsMutation,
  ConsignmentTermsPreview,
  ProposedConsignmentFeeSchedule,
} from '@tcvs/shared';
import {
  CONSIGNMENT_TERMS_MAX_CARDS,
  PROPOSED_CONSIGNMENT_FEE_SCHEDULE,
  proposedConsignmentFeeScheduleIsValid,
  consignmentFeePolicyIsValid,
} from '@tcvs/shared';
import { FeeProposalEditor, FeeProposalSummary } from './ConsignmentFeeProposal';
import { useConsignmentApi } from './consignment-api';
import { consignmentErrorMessage, formatMoneyMinor } from './consignment-ui';

/** Empty is intentionally unknown; do not coerce it into a zero commission. */
export function termsDecimal(value: string): number | undefined {
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return undefined;
  const [whole, fraction = ''] = value.split('.');
  const minor = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(minor) ? minor : undefined;
}

function Economics({
  terms,
}: {
  terms: {
    commissionBps: number | null;
    fixedFeeMinor: number;
    payoutHoldDays: number;
    feePolicy?: ConsignmentFeePolicy;
  };
}) {
  if (terms.feePolicy) {
    const p = terms.feePolicy;
    return (
      <span>
        Whole-price payout tiers:{' '}
        {p.tiers
          .map((t) => `${formatMoneyMinor(t.minimumGrossMinor)}+: ${t.payoutBps / 100}%`)
          .join('; ')}
        . Single-card listing {formatMoneyMinor(p.soldSingleCardFeeMinor)}; sealed listing{' '}
        {formatMoneyMinor(p.soldSealedWaxFeeMinor)}; unsold {formatMoneyMinor(p.unsoldItemFeeMinor)}{' '}
        plus listing/service; Express {formatMoneyMinor(p.expressSurchargeMinor)}; Premium{' '}
        {formatMoneyMinor(p.premiumSurchargeMinor)}. Selected: {p.selectedItemType},{' '}
        {p.selectedService}. $0 sold-item floor. Partial refunds:{' '}
        {p.refundPolicy === 'ORIGINAL_TIER_REVERSE_FULL' ? 'original tier' : 'recalculate tier'};
        full refunds reverse all fees. {terms.payoutHoldDays} additional calendar hold days.
      </span>
    );
  }
  return (
    <span>
      {terms.commissionBps === null
        ? 'Commission: Not agreed'
        : `${(terms.commissionBps / 100).toFixed(2)}% commission`}{' '}
      · {formatMoneyMinor(terms.fixedFeeMinor)} fixed fee · {terms.payoutHoldDays} additional
      calendar hold days
    </span>
  );
}

/** Key by owner even when used outside ConsignmentPage, so pending owner state cannot leak. */
export function ConsignmentTerms(props: { consignorId: string; consignorName: string }) {
  return <TermsWorkspace key={props.consignorId} {...props} />;
}

function TermsWorkspace({
  consignorId,
  consignorName,
}: {
  consignorId: string;
  consignorName: string;
}) {
  const api = useConsignmentApi();
  const [drafts, setDrafts] = useState<ConsignmentTermsDraft[]>([]);
  const [draftCursor, setDraftCursor] = useState<string>();
  const [agreements, setAgreements] = useState<ConsignmentAgreement[]>([]);
  const [inventory, setInventory] = useState<ConsignmentInventoryItem[]>([]);
  const [inventoryCursor, setInventoryCursor] = useState<string>();
  const [editor, setEditor] = useState<{
    draft?: ConsignmentTermsDraft;
    predecessor?: ConsignmentAgreement;
    key: string;
  }>();
  const [selected, setSelected] = useState<string[]>([]);
  const [target, setTarget] = useState('');
  const [preview, setPreview] = useState<ConsignmentTermsPreview>();
  const [reason, setReason] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [history, setHistory] = useState<CardTermsDetail>();
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const request = useRef<{ signature: string; requestId: string }>();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const [d, a, i] = await Promise.all([
      api.listTermsDrafts(consignorId),
      api.listAgreements(consignorId),
      api.listInventory(consignorId),
    ]);
    if (!d.ok) throw new Error(consignmentErrorMessage(d));
    if (!a.ok) throw new Error(consignmentErrorMessage(a));
    if (!i.ok) throw new Error(consignmentErrorMessage(i));
    if (!mounted.current) return;
    setDrafts(d.data.items);
    setDraftCursor(d.data.cursor);
    setAgreements(a.data.items);
    setInventory(i.data.items);
    setInventoryCursor(i.data.cursor);
    setReady(true);
  }, [api, consignorId]);
  useEffect(() => {
    void load().catch((e) => {
      if (mounted.current) setError(e.message);
    });
  }, [load]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await action();
    } catch (e) {
      if (mounted.current)
        setError(e instanceof Error ? e.message : 'Unable to complete this operation.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  const invalidate = () => {
    setPreview(undefined);
    setReviewed(false);
    request.current = undefined;
  };
  const apply = async () => {
    if (!preview || !reviewed || !reason.trim()) return;
    const body = {
      operation: 'APPLY_CARD_TERMS' as const,
      consignorId,
      agreementId: preview.agreement.agreementId,
      cardIds: preview.cards.map((c) => c.cardId),
      fingerprint: preview.fingerprint,
      reason: reason.trim(),
    };
    const signature = JSON.stringify(body);
    if (request.current?.signature !== signature)
      request.current = { signature, requestId: crypto.randomUUID() };
    const result = await api.mutate<ConsignmentTermsApplyResult>({
      ...body,
      requestId: request.current.requestId,
    });
    if (!result.ok)
      throw new Error(
        `${consignmentErrorMessage(result)}. Retry unchanged to recover the same request, or review a fresh preview before changing terms.`,
      );
    invalidate();
    setSelected([]);
    setReason('');
    setHistory(undefined);
    setNotice(
      `Terms applied to ${result.data.cardIds.length} cards, effective ${result.data.effectiveAt}. Intake defaults and completed sales are unchanged.`,
    );
    await load();
  };

  return (
    <>
      <h2>Terms for {consignorName}</h2>
      <p>
        Drafts are proposals. Publishing confirms agreed terms but does not change existing cards.
        Each published agreement is immutable.
      </p>
      {error && (
        <p role="alert" className="tcvs-error">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="tcvs-success">
          {notice}
        </p>
      )}
      {!ready && !error && <p>Loading terms and inventory…</p>}
      <section className="tcvs-card">
        <h3>Draft agreements</h3>
        <button disabled={busy || !ready} onClick={() => setEditor({ key: crypto.randomUUID() })}>
          New draft
        </button>
        {drafts
          .filter((d) => d.status === 'DRAFT')
          .map((d) => (
            <div key={d.draftId}>
              <strong>{d.name}</strong> · Draft ·{' '}
              {d.proposedFeeSchedule ? (
                <FeeProposalSummary schedule={d.proposedFeeSchedule} />
              ) : (
                <Economics terms={d} />
              )}{' '}
              <button disabled={busy} onClick={() => setEditor({ draft: d, key: d.draftId })}>
                Edit {d.name}
              </button>
            </div>
          ))}
        {draftCursor && (
          <button
            disabled={busy}
            onClick={() =>
              void run(async () => {
                const result = await api.listTermsDrafts(consignorId, draftCursor);
                if (!result.ok) throw new Error(consignmentErrorMessage(result));
                setDrafts((current) => [...current, ...result.data.items]);
                setDraftCursor(result.data.cursor);
              })
            }
          >
            More drafts
          </button>
        )}
        {editor && (
          <DraftEditor
            key={editor.key}
            consignorId={consignorId}
            draft={editor.draft}
            predecessor={editor.predecessor}
            onBusy={setBusy}
            onClose={() => setEditor(undefined)}
            onSaved={async () => {
              await load();
            }}
            onPublished={async () => {
              setEditor(undefined);
              await load();
            }}
          />
        )}
      </section>
      <section className="tcvs-card">
        <h3>Published agreements</h3>
        {agreements.map((a) => (
          <div key={a.agreementId}>
            <strong>{a.name}</strong> · {a.status} · Version {a.version} · <Economics terms={a} />
            <p>
              Effective {a.effectiveAt}
              {a.predecessorAgreementId ? ` · Revises ${a.predecessorAgreementId}` : ''}
            </p>
            {a.status === 'ACTIVE' && (
              <button
                disabled={busy}
                onClick={() => setEditor({ predecessor: a, key: crypto.randomUUID() })}
              >
                Create revision of {a.name}
              </button>
            )}
          </div>
        ))}
        {!agreements.length && <p>No published agreements.</p>}
      </section>
      <section className="tcvs-card">
        <h3>Review terms for selected cards</h3>
        <p>
          Select up to {CONSIGNMENT_TERMS_MAX_CARDS} cards. Only eligible unsold, uncommitted cards
          can change terms; the server checks eligibility at preview and application. Effective time
          is recorded when applied.
        </p>
        <fieldset disabled={busy || !ready}>
          <label className="tcvs-field">
            <span>Proposed published terms</span>
            <select
              aria-label="Proposed published terms"
              value={target}
              onChange={(e) => {
                setTarget(e.target.value);
                invalidate();
              }}
            >
              <option value="">Select terms</option>
              {agreements
                .filter((a) => a.status === 'ACTIVE')
                .map((a) => (
                  <option key={a.agreementId} value={a.agreementId}>
                    {a.name} · v{a.version}
                  </option>
                ))}
            </select>
          </label>
          <p>{selected.length} cards selected</p>
          <div className="tcvs-table-wrap">
            <table className="tcvs-table">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Card</th>
                  <th>Current terms</th>
                  <th>History</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((card) => (
                  <tr key={card.cardId}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${card.shortCode ?? card.cardId}`}
                        checked={selected.includes(card.cardId)}
                        disabled={
                          !selected.includes(card.cardId) &&
                          selected.length >= CONSIGNMENT_TERMS_MAX_CARDS
                        }
                        onChange={(e) => {
                          setSelected((ids) =>
                            e.target.checked
                              ? [...ids, card.cardId]
                              : ids.filter((id) => id !== card.cardId),
                          );
                          invalidate();
                        }}
                      />
                    </td>
                    <td>
                      {card.shortCode ?? card.cardId} ·{' '}
                      {card.identityDisplay || 'Awaiting identification'}
                    </td>
                    <td>
                      {agreements.find((a) => a.agreementId === card.agreementId)?.name ??
                        card.agreementId}
                    </td>
                    <td>
                      <button
                        onClick={() =>
                          void run(async () => {
                            setHistory(undefined);
                            const result = await api.getCardTerms(consignorId, card.cardId);
                            if (!result.ok) throw new Error(consignmentErrorMessage(result));
                            setHistory(result.data);
                          })
                        }
                      >
                        History for {card.shortCode ?? card.cardId}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {inventoryCursor && (
            <button
              onClick={() =>
                void run(async () => {
                  const result = await api.listInventory(consignorId, inventoryCursor);
                  if (!result.ok) throw new Error(consignmentErrorMessage(result));
                  setInventory((current) => [
                    ...current,
                    ...result.data.items.filter(
                      (item) => !current.some((c) => c.cardId === item.cardId),
                    ),
                  ]);
                  setInventoryCursor(result.data.cursor);
                })
              }
            >
              More owned cards
            </button>
          )}
          <button
            disabled={!target || !selected.length}
            onClick={() =>
              void run(async () => {
                invalidate();
                const result = await api.mutate<ConsignmentTermsPreview>({
                  operation: 'PREVIEW_CARD_TERMS',
                  consignorId,
                  agreementId: target,
                  cardIds: selected,
                });
                if (!result.ok) throw new Error(consignmentErrorMessage(result));
                setPreview(result.data);
              })
            }
          >
            Preview changes
          </button>
        </fieldset>
        {preview && (
          <div>
            <h4>Review {preview.cards.length} card changes</h4>
            <p>
              Proposed: {preview.agreement.name} · <Economics terms={preview.agreement} />
            </p>
            <ul>
              {preview.cards.map((c) => (
                <li key={c.cardId}>
                  {c.cardId}: {c.currentAgreement.name} · <Economics terms={c.currentAgreement} /> →{' '}
                  {preview.agreement.name}
                </li>
              ))}
            </ul>
            <fieldset disabled={busy}>
              <label className="tcvs-field">
                <span>Reason for changing these cards</span>
                <textarea
                  maxLength={500}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setReviewed(false);
                  }}
                />
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={reviewed}
                  onChange={(e) => setReviewed(e.target.checked)}
                />{' '}
                I reviewed these exact cards and their current and proposed terms.
              </label>
              <button disabled={!reviewed || !reason.trim()} onClick={() => void run(apply)}>
                Apply reviewed terms
              </button>
            </fieldset>
          </div>
        )}
        {history && (
          <div>
            <h4>Terms history: {history.cardId}</h4>
            <p>
              Actual current agreement:{' '}
              {agreements.find((a) => a.agreementId === history.agreementId)?.name ??
                history.agreementId}{' '}
              · Revision {history.termsRevision ?? 'Legacy'}
            </p>
            {!history.revisionEligible && (
              <p>Cannot revise: {history.blockedReason ?? 'Not eligible for terms changes'}</p>
            )}
            <ol>
              {history.history.map((h) => (
                <li key={h.termsRevision}>
                  {h.terms.name} · <Economics terms={h.terms} />
                  <p>
                    Effective {h.effectiveAt} · {h.actor} · {h.reason}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>
    </>
  );
}

function DraftEditor({
  consignorId,
  draft,
  predecessor,
  onSaved,
  onBusy,
  onPublished,
  onClose,
}: {
  consignorId: string;
  draft?: ConsignmentTermsDraft;
  predecessor?: ConsignmentAgreement;
  onSaved: () => Promise<void>;
  onBusy: (busy: boolean) => void;
  onPublished: () => Promise<void>;
  onClose: () => void;
}) {
  const api = useConsignmentApi();
  const initial = draft ?? predecessor;
  const [saved, setSaved] = useState(draft);
  const [proposal, setProposal] = useState<ProposedConsignmentFeeSchedule | undefined>(
    initial?.feePolicy
      ? { ...initial.feePolicy, kind: 'PROPOSED_FULL_SCHEDULE_V1' }
      : draft?.proposedFeeSchedule,
  );
  const [refundPolicy, setRefundPolicy] = useState<ConsignmentFeePolicy['refundPolicy'] | ''>(
    initial?.feePolicy?.refundPolicy ?? '',
  );
  const candidatePolicy =
    proposal && refundPolicy
      ? {
          ...proposal,
          kind: 'FULL_SCHEDULE_V1',
          zeroProceedsFloor: true,
          unsoldFeeMode: 'ADD_LISTING_AND_SERVICE',
          refundPolicy,
        }
      : undefined;
  const feePolicy =
    consignmentFeePolicyIsValid(candidatePolicy) &&
    candidatePolicy.selectedItemType === 'SINGLE_CARD'
      ? candidatePolicy
      : undefined;
  const [name, setName] = useState(
    draft?.name ?? (predecessor ? `${predecessor.name} — revision` : 'Initial consignment v1'),
  );
  const [commission, setCommission] = useState(
    initial?.commissionBps == null ? '' : (initial.commissionBps / 100).toFixed(2),
  );
  const [fee, setFee] = useState(((initial?.fixedFeeMinor ?? 0) / 100).toFixed(2));
  const [hold, setHold] = useState(String(initial?.payoutHoldDays ?? 0));
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [draftId] = useState(() => draft?.draftId ?? crypto.randomUUID());
  const pending = useRef<{ signature: string; requestId: string }>();
  const commissionBps = feePolicy ? null : commission === '' ? null : termsDecimal(commission);
  const fixedFeeMinor = feePolicy ? 0 : termsDecimal(fee);
  const payoutHoldDays = /^\d+$/.test(hold) ? Number(hold) : undefined;
  const valid =
    (!proposal || proposedConsignmentFeeScheduleIsValid(proposal)) &&
    !!name.trim() &&
    commissionBps !== undefined &&
    (commissionBps === null || commissionBps <= 10000) &&
    fixedFeeMinor !== undefined &&
    fixedFeeMinor <= 100000000 &&
    payoutHoldDays !== undefined &&
    payoutHoldDays <= 365;
  const unchanged =
    saved &&
    JSON.stringify(saved.feePolicy) === JSON.stringify(feePolicy) &&
    JSON.stringify(saved.proposedFeeSchedule) ===
      JSON.stringify(feePolicy ? undefined : proposal) &&
    saved.name === name.trim() &&
    saved.commissionBps === commissionBps &&
    saved.fixedFeeMinor === fixedFeeMinor &&
    saved.payoutHoldDays === payoutHoldDays;
  const act = async (publish: boolean) => {
    if (
      !valid ||
      (publish &&
        (!unchanged ||
          (commissionBps === null && !feePolicy) ||
          !agreed ||
          (!!proposal && !feePolicy)))
    )
      return;
    setBusy(true);
    onBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const body = publish
        ? {
            operation: 'PUBLISH_TERMS_DRAFT' as const,
            draftId,
            consignorId,
            expectedVersion: saved!.recordVersion,
            termsAgreed: true as const,
          }
        : {
            operation: 'SAVE_TERMS_DRAFT' as const,
            ...(feePolicy ? { feePolicy } : proposal ? { proposedFeeSchedule: proposal } : {}),
            draftId,
            consignorId,
            expectedVersion: saved?.recordVersion ?? 0,
            name: name.trim(),
            commissionBps: commissionBps!,
            fixedFeeMinor: fixedFeeMinor!,
            payoutHoldDays: payoutHoldDays!,
            ...((saved?.predecessorAgreementId ?? predecessor?.agreementId)
              ? {
                  predecessorAgreementId: saved?.predecessorAgreementId ?? predecessor?.agreementId,
                }
              : {}),
          };
      const signature = JSON.stringify(body);
      if (pending.current?.signature !== signature)
        pending.current = { signature, requestId: crypto.randomUUID() };
      const mutation = {
        ...body,
        requestId: pending.current.requestId,
      } as ConsignmentTermsMutation;
      if (publish) {
        const result = await api.mutate<ConsignmentAgreement>(mutation);
        if (!result.ok) throw new Error(consignmentErrorMessage(result));
        await onPublished();
      } else {
        const result = await api.mutate<ConsignmentTermsDraft>(mutation);
        if (!result.ok) throw new Error(consignmentErrorMessage(result));
        setSaved(result.data);
        setAgreed(false);
        pending.current = undefined;
        setNotice('Draft saved. It is not available for intake or card assignment.');
        await onSaved();
      }
    } catch (e) {
      setError(
        `${e instanceof Error ? e.message : 'Operation failed'}. Retry unchanged if the response was interrupted. For a version conflict, close and reopen the latest draft.`,
      );
    } finally {
      setBusy(false);
      onBusy(false);
    }
  };
  return (
    <section aria-label="Draft terms editor">
      <h4>{draft ? 'Edit draft' : predecessor ? 'New revision draft' : 'New draft'}</h4>
      {(draft?.predecessorAgreementId ?? predecessor?.agreementId) && (
        <p>Predecessor: {draft?.predecessorAgreementId ?? predecessor?.agreementId}</p>
      )}
      {error && (
        <p role="alert" className="tcvs-error">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {proposal && <FeeProposalSummary schedule={feePolicy ?? proposal} />}
      <fieldset disabled={busy} onChange={() => setAgreed(false)}>
        {!proposal && (
          <button
            onClick={() => {
              setProposal(
                JSON.parse(
                  JSON.stringify({ ...PROPOSED_CONSIGNMENT_FEE_SCHEDULE, zeroProceedsFloor: true }),
                ) as ProposedConsignmentFeeSchedule,
              );
              setCommission('');
              setFee('0.00');
              setAgreed(false);
            }}
          >
            Use reference schedule
          </button>
        )}
        {proposal && (
          <>
            <FeeProposalEditor schedule={proposal} onChange={setProposal} />
            <label className="tcvs-field">
              <span>Refund policy</span>
              <select
                aria-label="Refund policy"
                value={refundPolicy}
                onChange={(e) =>
                  setRefundPolicy(e.target.value as ConsignmentFeePolicy['refundPolicy'] | '')
                }
              >
                <option value="">Select agreed refund policy</option>
                <option value="ORIGINAL_TIER_REVERSE_FULL">
                  Keep original tier for partial refunds; reverse all fees for full refunds
                </option>
                <option value="RETIER_REVERSE_FULL">
                  Recalculate tier for partial refunds; reverse all fees for full refunds
                </option>
              </select>
            </label>
            {!feePolicy && (
              <p>Select single cards, a service and an agreed refund policy before publishing.</p>
            )}
          </>
        )}
        <label className="tcvs-field">
          <span>Agreement name</span>
          <input
            aria-label="Agreement name"
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {!proposal && (
          <>
            <label className="tcvs-field">
              <span>Commission % (blank means Not agreed)</span>
              <input
                aria-label="Commission percentage"
                inputMode="decimal"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
              />
            </label>
            <p>
              {commission === ''
                ? 'Commission: Not agreed'
                : commissionBps === 0
                  ? 'Commission: deliberately 0%'
                  : 'Commission is the business share of the sale.'}
            </p>
            <label className="tcvs-field">
              <span>Fixed fee in USD</span>
              <input
                aria-label="Fixed fee"
                inputMode="decimal"
                value={fee}
                onChange={(e) => setFee(e.target.value)}
              />
            </label>
          </>
        )}
        <label className="tcvs-field">
          <span>Additional calendar hold days</span>
          <input
            aria-label="Additional calendar hold days"
            inputMode="numeric"
            value={hold}
            onChange={(e) => setHold(e.target.value)}
          />
        </label>
        {!valid && (
          <p>
            Check that tiers increase, payouts are 0–100%, and fees are nonnegative with at most two
            decimal places. Enter a name, commission from 0–100% or leave it blank, a nonnegative
            fee with at most two decimal places, and 0–365 whole hold days.
          </p>
        )}
        <button disabled={!valid || !!unchanged} onClick={() => void act(false)}>
          Save draft
        </button>
      </fieldset>
      <label>
        <input
          type="checkbox"
          disabled={
            busy ||
            !unchanged ||
            (commissionBps === null && !feePolicy) ||
            (!!proposal && !feePolicy)
          }
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
        />{' '}
        These saved terms have been agreed with the consignor.
      </label>
      <button
        disabled={
          busy ||
          !unchanged ||
          (commissionBps === null && !feePolicy) ||
          !agreed ||
          (!!proposal && !feePolicy)
        }
        onClick={() => void act(true)}
      >
        Publish agreed terms
      </button>{' '}
      <button disabled={busy} onClick={onClose}>
        Close draft
      </button>
      <p>
        Publishing does not apply this agreement to existing cards. Use the separate card preview
        after publishing.
      </p>
    </section>
  );
}
